import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Grid,
    IconButton,
    TextField,
    Typography,
    Chip,
    Card,
    CardContent,
    InputAdornment,
    Stack,
    Alert,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import {
    Send as SendIcon,
    ArrowBack as ArrowBackIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
    Draw as DrawIcon,
    Clear as ClearIcon,
    PersonSearch as PersonSearchIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JOB_STORE, BUS_INSPECTION_STORE } from '../../../config/constants';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import SelectEmployeesAndTempsModal from '../../jha/SelectEmployeesAndTempsModal';
import { generateUUID } from '../../../utils/uuid';
import Swal from 'sweetalert2';
import { busInspectionService } from '../../../services/busInspectionService';
import useDictation from '../../../hooks/useDictation';
import axiosInstance from '../../../axiosInstance';

const STATUS_DRAFT = 'draft';
const STATUS_SUBMITTED = 'submitted';

const BusInspectionForm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const uuid = searchParams.get('uuid');

    const userFullName = localStorage.getItem('employeeFullName') || '';
    const userId = localStorage.getItem('employeeId') || '';

    const [jobs, setJobs] = useState([]);
    const [form, setForm] = useState({
        uuid: uuid || generateUUID(),
        jobCode: '',
        jobName: '',
        location: '',
        
        // Header fields
        installationContractor: userFullName,
        bussduct: '',
        busPlugNumber: '',
        columnLocation: '',
        loadServed: '',
        busPlugSize: '',
        testDate: dayjs().format('YYYY-MM-DD'),
        
        // Bus Plug Checks
        busPlugLabeled: '', // 'temp' or 'permanent'
        busPlugLabeled_comment: '',
        
        switchSupport: '',
        switchSupport_comment: '',
        
        switchGeneralInstallation: '',
        switchGeneralInstallation_comment: '',
        
        plugAssistEngaged: '',
        plugAssistEngaged_comment: '',
        
        functionSwitch: '',
        functionSwitch_comment: '',
        
        fuseSize: '',
        fuseSize_comment: '',
        
        fuseType: '',
        fuseType_comment: '',
        
        conductorSize: '',
        conductorSize_comment: '',
        
        connectionTightnessBusPlug: '',
        connectionTightnessBusPlug_comment: '',
        
        connectionTightnessLugs: '',
        connectionTightnessLugs_comment: '',
        
        conduitSupportBusPlug: false,
        conduitSupportBusPlug_comment: '',
        
        // Panel Checks
        panelLabeled: '', // 'temp' or 'permanent'
        panelLabeled_comment: '',
        
        arcFlashSticker: '', // 'yes' or 'no'
        arcFlashSticker_comment: '',
        
        // Conductor Phasing
        phasing_A_brown: '',
        phasing_A_brown_comment: '',
        
        phasing_B_orange: '',
        phasing_B_orange_comment: '',
        
        phasing_C_yellow: '',
        phasing_C_yellow_comment: '',
        
        phasing_GRD_green: '',
        phasing_GRD_green_comment: '',
        
        phasing_Neutral_white: '',
        phasing_Neutral_white_comment: '',
        
        additionalWiresLineSide: '',
        additionalWiresLineSide_comment: '',
        
        lineSideDistributionBlocks: '',
        lineSideDistributionBlocks_comment: '',
        
        plexiglassCover: '',
        plexiglassCover_comment: '',
        
        connectionTightnessPanelLugs: '',
        connectionTightnessPanelLugs_comment: '',
        
        conduitBushingInstallation: '',
        conduitBushingInstallation_comment: '',
        
        groundCable: '',
        groundCable_comment: '',
        
        // Megger Readings (in MΩ)
        megger_AB: '',
        megger_BC: '',
        megger_CA: '',
        megger_A_GRD: '',
        megger_B_GRD: '',
        megger_C_GRD: '',
        megger_GRD_Neutral: '',
        hasNeutral: false,
        
        // Disconnect check
        disconnectCheckConfirmed: false,
        
        // Voltage Readings
        systemVoltage: '480/277', // '480/277' or '208/120'
        voltage_AB: '',
        voltage_BC: '',
        voltage_CA: '',
        voltage_A_GRD: '',
        voltage_B_GRD: '',
        voltage_C_GRD: '',
        
        // Phase Rotation
        phaseRotation: '', // 'CW' or 'CCW'
        phaseRotation_comment: '',
        
        // NEC
        anyNECViolations: '', // 'Y' or 'N'
        anyNECViolations_comment: '',
        
        // Signatures
        clientSignatureId: '',
        clientSignatureName: '',
        clientSignature: '',
        clientSignatureDate: dayjs().format('YYYY-MM-DD'),
        
        shopTeamSignatureId: '',
        shopTeamSignatureName: '',
        shopTeamSignature: '',
        shopTeamSignatureDate: dayjs().format('YYYY-MM-DD'),
        
        contractorSignatureId: userId,
        contractorSignatureName: userFullName,
        contractorSignature: '',
        contractorSignatureDate: dayjs().format('YYYY-MM-DD'),
        
        status: STATUS_DRAFT,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    });

    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(true);

    // Dictation
    const [activeField, setActiveField] = useState(null);
    const currentFieldRef = useRef(null);
    const sigPadRef = useRef(null);
    
    const { 
        isSupported: dictationSupported, 
        isListening, 
        start: startDictation, 
        stop: stopDictation 
    } = useDictation({
        lang: 'en-US',
        interimResults: false,
        continuous: false,
        safetyTimeoutMs: 15000,
        prime: true,
    });

    const toggleDictation = async (fieldName) => {
        if (submitted) return;
        
        const fieldIsActive = isListening && activeField === fieldName;
        if (fieldIsActive) {
            stopDictation();
            setActiveField(null);
            currentFieldRef.current = null;
            return;
        }
        
        // Stop previous dictation if switching fields
        if (isListening) {
            stopDictation();
        }
        
        currentFieldRef.current = fieldName;
        setActiveField(fieldName);
        
        await startDictation({
            onResult: (transcript) => {
                if (!transcript || !currentFieldRef.current) return;
                
                const field = currentFieldRef.current;
                const currentValue = form[field] || '';
                const newValue = currentValue + (currentValue ? ' ' : '') + transcript;
                
                const updated = {
                    ...form,
                    [field]: newValue
                };
                setForm(updated);
                queueSave(updated);
            },
            onEnd: () => {
                setActiveField(null);
                currentFieldRef.current = null;
            }
        });
    };

    // Signature modals
    const [clientSignatureModalOpen, setClientSignatureModalOpen] = useState(false);
    const [shopTeamSignatureModalOpen, setShopTeamSignatureModalOpen] = useState(false);
    const [contractorSignatureModalOpen, setContractorSignatureModalOpen] = useState(false);
    
    // Employee/Temp modals
    const [contractorModalOpen, setContractorModalOpen] = useState(false);
    const [contractorSignatureModalEmployeeOpen, setContractorSignatureModalEmployeeOpen] = useState(false);

    // Sort jobs by Job number in descending order
    const sortedJobs = useMemo(() => {
        const jobsWithLabels = jobs.map(job => ({
            ...job,
            label: `${job.Job} - ${job.JobName}`
        }));
        return jobsWithLabels.sort((a, b) => {
            const jobA = parseInt(a.Job) || 0;
            const jobB = parseInt(b.Job) || 0;
            return jobB - jobA;
        });
    }, [jobs]);

    useEffect(() => {
        loadJobs();
        if (uuid) {
            loadForm(uuid);
        } else {
            // Set default comments on new form
            setForm(prev => ({
                ...prev,
                conductorSize_comment: "Provide 5' section of LFMC(Seal-Tite) to busplug. Rigid conduit",
                additionalWiresLineSide_comment: "All wire must have colored insulation. Phase tape is not acc.",
                conduitBushingInstallation_comment: "No EMT Conduit",
                anyNECViolations_comment: "Additional Actions by Contractor Required to Meet NEC"
            }));
            setLoading(false);
        }
    }, [uuid]);

    const loadJobs = async () => {
        try {
            const db = await initializeDB();
            const jobList = await db.getAll(JOB_STORE);
            setJobs(jobList || []);
        } catch (error) {
            console.error('Error loading jobs:', error);
        }
    };

    const loadForm = async (id) => {
        try {
            const data = await busInspectionService.getFromIndexedDB(id);
            if (data) {
                setForm(data);
                setSubmitted(data.status === STATUS_SUBMITTED);
            }
        } catch (error) {
            console.error('Error loading form:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFieldChange = (field, value) => {
        const updated = {
            ...form,
            [field]: value
        };
        setForm(updated);
        setErrors(prev => ({ ...prev, [field]: null }));
        queueSave(updated);
    };

    const handleJobChange = (event, newValue) => {
        if (!newValue) {
            const updated = {
                ...form,
                jobCode: '',
                jobName: '',
                location: ''
            };
            setForm(updated);
            queueSave(updated);
            return;
        }

        const updated = {
            ...form,
            jobCode: newValue.Job || '',
            jobName: newValue.JobName || '',
            location: newValue.Location || newValue.location || ''
        };
        setForm(updated);
        queueSave(updated);
    };

    const handleCheckChange = (field, value) => {
        const updated = {
            ...form,
            [field]: value
        };
        setForm(updated);
        queueSave(updated);
    };

    const saveTimeoutRef = useRef(null);
    const queueSave = (formData) => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => {
            saveDraft(formData);
        }, 1000);
    };

    const saveDraft = async (formData) => {
        if (submitted) return;
        
        const dataToSave = formData || form;
        
        try {
            const updatedForm = {
                ...dataToSave,
                updatedAt: new Date().toISOString()
            };
            await busInspectionService.saveToIndexedDB(updatedForm);
        } catch (error) {
            console.error('Error saving draft:', error);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!form.jobCode) newErrors.jobCode = 'Job is required';
        if (!form.busPlugLabeled) newErrors.busPlugLabeled = 'Required';
        if (!form.fuseSize) newErrors.fuseSize = 'Required';
        if (!form.fuseType) newErrors.fuseType = 'Required';
        if (!form.conductorSize) newErrors.conductorSize = 'Required';
        if (!form.connectionTightnessBusPlug) newErrors.connectionTightnessBusPlug = 'Required';
        if (!form.connectionTightnessLugs) newErrors.connectionTightnessLugs = 'Required';
        // conduitSupportBusPlug is optional (checkbox)
        if (!form.panelLabeled) newErrors.panelLabeled = 'Required';
        
        // Voltage validation removed
        
        // disconnectCheckConfirmed is optional
        
        if (!form.anyNECViolations) newErrors.anyNECViolations = 'Required';
        
        if (!form.clientSignature) newErrors.clientSignature = 'Client signature required';
        // shopTeamSignature is optional (Client Area Manager)
        if (!form.contractorSignature) newErrors.contractorSignature = 'Contractor signature required';
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please fill in all required fields correctly.'
            });
            return;
        }

        const result = await Swal.fire({
            icon: 'question',
            title: 'Submit Bus Inspection Report?',
            text: 'This will finalize the report and it cannot be edited after submission.',
            showCancelButton: true,
            confirmButtonText: 'Submit',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        setSaving(true);
        try {
            const submittedForm = {
                ...form,
                status: STATUS_SUBMITTED,
                testDate: form.testDate || dayjs().format('YYYY-MM-DD'),
                clientSignatureDate: form.clientSignatureDate || dayjs().format('YYYY-MM-DD'),
                shopTeamSignatureDate: form.shopTeamSignatureDate || dayjs().format('YYYY-MM-DD'),
                contractorSignatureDate: form.contractorSignatureDate || dayjs().format('YYYY-MM-DD'),
                updatedAt: new Date().toISOString()
            };

            // Save to IndexedDB
            await busInspectionService.saveToIndexedDB(submittedForm);
            
            // Submit to backend
            const response = await axiosInstance.post('/bus-inspection/submit', submittedForm);
            console.log('Server response:', response.data);

            setForm(submittedForm);
            setSubmitted(true);

            Swal.fire({
                icon: 'success',
                title: 'Submitted!',
                text: 'Bus Inspection Report has been submitted successfully.'
            });

            setTimeout(() => {
                navigate('/electrical/forms/bus-inspection/list');
            }, 1500);
        } catch (error) {
            console.error('Error submitting form:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Failed to submit the report. Please try again.'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleSignatureClick = (type) => {
        let nameField = '';
        if (type === 'client') nameField = 'clientSignatureName';
        else if (type === 'shopTeam') nameField = 'shopTeamSignatureName';
        else if (type === 'contractor') nameField = 'contractorSignatureName';
        
        if (!form[nameField]) {
            Swal.fire({
                icon: 'warning',
                title: 'Name Required',
                text: 'Please enter a name before adding signature.'
            });
            return;
        }
        
        if (type === 'client') setClientSignatureModalOpen(true);
        else if (type === 'shopTeam') setShopTeamSignatureModalOpen(true);
        else if (type === 'contractor') setContractorSignatureModalOpen(true);
    };

    const handleSignatureAccept = (type) => {
        if (!sigPadRef.current) {
            console.error('Signature pad ref not available');
            return;
        }

        const dataUrl = sigPadRef.current.toDataURL();
        const currentDate = dayjs().format('YYYY-MM-DD');
        
        let updated;
        
        if (type === 'client') {
            updated = {
                ...form,
                clientSignature: dataUrl,
                clientSignatureDate: form.clientSignatureDate || currentDate,
                testDate: form.testDate || currentDate
            };
            setClientSignatureModalOpen(false);
        } else if (type === 'shopTeam') {
            updated = {
                ...form,
                shopTeamSignature: dataUrl,
                shopTeamSignatureDate: form.shopTeamSignatureDate || currentDate
            };
            setShopTeamSignatureModalOpen(false);
        } else if (type === 'contractor') {
            updated = {
                ...form,
                contractorSignature: dataUrl,
                contractorSignatureDate: form.contractorSignatureDate || currentDate
            };
            setContractorSignatureModalOpen(false);
        }
        
        setForm(updated);
        queueSave(updated);
    };
    
    const getSignatureName = (type) => {
        if (type === 'client') return form.clientSignatureName || '';
        if (type === 'shopTeam') return form.shopTeamSignatureName || '';
        if (type === 'contractor') return form.contractorSignatureName || '';
        return '';
    };
    
    const handleSelectContractor = (selectedArray) => {
        if (!selectedArray || selectedArray.length === 0) return;
        const person = selectedArray[0];
        const updated = {
            ...form,
            installationContractor: person.FullName || person.name || '',
            contractorSignatureName: person.FullName || person.name || '',
            contractorSignatureId: person.id || person.employeeId || ''
        };
        setForm(updated);
        queueSave(updated);
        setContractorModalOpen(false);
    };
    
    const handleSelectContractorSignature = (selectedArray) => {
        if (!selectedArray || selectedArray.length === 0) return;
        const person = selectedArray[0];
        const updated = {
            ...form,
            contractorSignatureName: person.FullName || person.name || '',
            contractorSignatureId: person.id || person.employeeId || ''
        };
        setForm(updated);
        queueSave(updated);
        setContractorSignatureModalEmployeeOpen(false);
    };

    // Render functions - single line style like Job Site Safety Inspection
    const renderCheckItem = (field, label) => {
        const isFieldListening = isListening && activeField === `${field}_comment`;
        const isChecked = form[field] === 'Y';
        
        return (
            <Grid item xs={12} key={field}>
                <Box sx={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    mb: 1,
                    pb: 1,
                    borderBottom: '1px solid #f0f0f0'
                }}>
                    <Box sx={{ flex: '0 0 35%', minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                            {label}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: '0 0 150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Button
                            variant={isChecked ? 'contained' : 'outlined'}
                            color="success"
                            size="small"
                            onClick={() => handleCheckChange(field, isChecked ? '' : 'Y')}
                            disabled={submitted}
                            sx={{ minWidth: 80, height: 32, fontSize: '0.75rem' }}
                        >
                            {isChecked ? '✓ Checkoff' : 'Checkoff'}
                        </Button>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <TextField
                            fullWidth
                            multiline
                            size="small"
                            placeholder="COMMENT/CORRECTION DATE"
                            value={form[`${field}_comment`] || ''}
                            onChange={(e) => handleFieldChange(`${field}_comment`, e.target.value)}
                            disabled={submitted}
                            InputProps={{
                                endAdornment: !submitted && (
                                    <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.2 }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleDictation(`${field}_comment`)}
                                            color={isFieldListening ? 'error' : 'primary'}
                                        >
                                            {isFieldListening ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ 
                                '& .MuiInputBase-input': { 
                                    fontSize: '0.75rem',
                                    padding: '4px 6px',
                                    lineHeight: '1.3'
                                },
                                '& .MuiInputBase-root': {
                                    alignItems: 'flex-start',
                                    padding: 0,
                                    minHeight: 'auto'
                                },
                                '& .MuiOutlinedInput-root': {
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center'
                                }
                            }}
                        />
                    </Box>
                </Box>
            </Grid>
        );
    };

    const renderRadioItem = (field, label, options) => {
        const isFieldListening = isListening && activeField === `${field}_comment`;
        
        return (
            <Grid item xs={12} key={field}>
                <Box sx={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    mb: 1,
                    pb: 1,
                    borderBottom: '1px solid #f0f0f0'
                }}>
                    <Box sx={{ flex: '0 0 35%', minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                            {label} {errors[field] && <span style={{ color: '#f44336' }}>*</span>}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: '0 0 150px' }}>
                        <Stack direction="row" spacing={0.5}>
                            {options.map(opt => (
                                <Button
                                    key={opt.value}
                                    variant={form[field] === opt.value ? 'contained' : 'outlined'}
                                    color="primary"
                                    size="small"
                                    onClick={() => handleCheckChange(field, opt.value)}
                                    disabled={submitted}
                                    sx={{ minWidth: opt.label.length > 4 ? 70 : 50, height: 32, fontSize: '0.7rem', padding: '4px 8px' }}
                                >
                                    {opt.label}
                                </Button>
                            ))}
                        </Stack>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <TextField
                            fullWidth
                            multiline
                            size="small"
                            placeholder="COMMENT"
                            value={form[`${field}_comment`] || ''}
                            onChange={(e) => handleFieldChange(`${field}_comment`, e.target.value)}
                            disabled={submitted}
                            InputProps={{
                                endAdornment: !submitted && (
                                    <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.2 }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleDictation(`${field}_comment`)}
                                            color={isFieldListening ? 'error' : 'primary'}
                                        >
                                            {isFieldListening ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ 
                                '& .MuiInputBase-input': { 
                                    fontSize: '0.75rem',
                                    padding: '4px 6px',
                                    lineHeight: '1.3'
                                },
                                '& .MuiInputBase-root': {
                                    alignItems: 'flex-start',
                                    padding: 0,
                                    minHeight: 'auto'
                                },
                                '& .MuiOutlinedInput-root': {
                                    padding: 0
                                }
                            }}
                        />
                    </Box>
                </Box>
            </Grid>
        );
    };

    const renderRadioItemWithComment = (field, label, options, commentPlaceholder = 'COMMENT') => {
        const isFieldListening = isListening && activeField === `${field}_comment`;
        
        return (
            <Grid item xs={12} key={field}>
                <Box sx={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    mb: 1,
                    pb: 1,
                    borderBottom: '1px solid #f0f0f0'
                }}>
                    <Box sx={{ flex: '0 0 35%', minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                            {label} {errors[field] && <span style={{ color: '#f44336' }}>*</span>}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: '0 0 150px' }}>
                        <Stack direction="row" spacing={0.5}>
                            {options.map(opt => (
                                <Button
                                    key={opt.value}
                                    variant={form[field] === opt.value ? 'contained' : 'outlined'}
                                    color="primary"
                                    size="small"
                                    onClick={() => handleCheckChange(field, opt.value)}
                                    disabled={submitted}
                                    sx={{ minWidth: opt.label.length > 4 ? 70 : 50, height: 32, fontSize: '0.7rem', padding: '4px 8px' }}
                                >
                                    {opt.label}
                                </Button>
                            ))}
                        </Stack>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <TextField
                            fullWidth
                            multiline
                            size="small"
                            placeholder={commentPlaceholder}
                            value={form[`${field}_comment`] || ''}
                            onChange={(e) => handleFieldChange(`${field}_comment`, e.target.value)}
                            disabled={submitted}
                            InputProps={{
                                endAdornment: !submitted && (
                                    <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.2 }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleDictation(`${field}_comment`)}
                                            color={isFieldListening ? 'error' : 'primary'}
                                        >
                                            {isFieldListening ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ 
                                '& .MuiInputBase-input': { 
                                    fontSize: '0.75rem',
                                    padding: '4px 6px',
                                    lineHeight: '1.3'
                                },
                                '& .MuiInputBase-root': {
                                    alignItems: 'flex-start',
                                    padding: 0,
                                    minHeight: 'auto'
                                },
                                '& .MuiOutlinedInput-root': {
                                    padding: 0
                                }
                            }}
                        />
                    </Box>
                </Box>
            </Grid>
        );
    };

    const renderInputWithComment = (field, label, required = false) => {
        const isFieldListening = isListening && activeField === `${field}_comment`;
        
        return (
            <Grid item xs={12} key={field}>
                <Box sx={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    mb: 1,
                    pb: 1,
                    borderBottom: '1px solid #f0f0f0'
                }}>
                    <Box sx={{ flex: '0 0 35%', minWidth: 0 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={label + (required ? ' *' : '')}
                            value={form[field] || ''}
                            onChange={(e) => handleFieldChange(field, e.target.value)}
                            disabled={submitted}
                            error={!!errors[field]}
                            helperText={errors[field]}
                            sx={{ 
                                '& .MuiInputBase-input': { 
                                    fontSize: '0.8rem',
                                    p: '6px 8px'
                                },
                                '& .MuiInputLabel-root': {
                                    fontSize: '0.8rem'
                                }
                            }}
                        />
                    </Box>
                    <Box sx={{ flex: '0 0 150px', display: 'flex', alignItems: 'center' }}>
                        {/* Empty space for alignment */}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <TextField
                            fullWidth
                            multiline
                            size="small"
                            placeholder="COMMENT"
                            value={form[`${field}_comment`] || ''}
                            onChange={(e) => handleFieldChange(`${field}_comment`, e.target.value)}
                            disabled={submitted}
                            InputProps={{
                                endAdornment: !submitted && (
                                    <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.2 }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleDictation(`${field}_comment`)}
                                            color={isFieldListening ? 'error' : 'primary'}
                                        >
                                            {isFieldListening ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ 
                                '& .MuiInputBase-input': { 
                                    fontSize: '0.75rem',
                                    padding: '4px 6px',
                                    lineHeight: '1.3'
                                },
                                '& .MuiInputBase-root': {
                                    alignItems: 'flex-start',
                                    padding: 0,
                                    minHeight: 'auto'
                                },
                                '& .MuiOutlinedInput-root': {
                                    padding: 0
                                }
                            }}
                        />
                    </Box>
                </Box>
            </Grid>
        );
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column', backgroundColor: '#f5f5f5' }}>
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', backgroundColor: '#fff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate('/electrical/forms/bus-inspection/list')} disabled={saving}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>Bus Inspection Report</Typography>
                </Box>
            </Box>

            {/* Form Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                    {/* Job Information & Header */}
                    <Card sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                            Job Information
                        </Typography>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={sortedJobs}
                                    getOptionLabel={(option) => option.label || ''}
                                    value={sortedJobs.find(j => j.Job === form.jobCode) || null}
                                    onChange={handleJobChange}
                                    disabled={submitted}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Job *"
                                            size="small"
                                            error={!!errors.jobCode}
                                            helperText={errors.jobCode}
                                            sx={{ 
                                                '& .MuiInputBase-input': { fontSize: '0.85rem' },
                                                '& .MuiInputLabel-root': { fontSize: '0.85rem' }
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Date"
                                    type="date"
                                    value={form.testDate}
                                    onChange={(e) => handleFieldChange('testDate', e.target.value)}
                                    disabled={submitted}
                                    InputLabelProps={{ shrink: true }}
                                    onClick={(e) => {
                                        if (!submitted) {
                                            e.currentTarget.querySelector('input')?.showPicker?.();
                                        }
                                    }}
                                    sx={{ 
                                        '& .MuiInputBase-input': { fontSize: '0.85rem' },
                                        cursor: submitted ? 'default' : 'pointer'
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Installation Contractor"
                                    value={form.installationContractor}
                                    onClick={() => {
                                        if (!submitted) {
                                            setContractorModalOpen(true);
                                        }
                                    }}
                                    InputProps={{
                                        readOnly: true,
                                        style: { cursor: submitted ? 'default' : 'pointer' },
                                        endAdornment: !submitted && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setContractorModalOpen(true)} size="small">
                                                    <PersonSearchIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    disabled={submitted}
                                    sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Bussduct"
                                    value={form.bussduct}
                                    onChange={(e) => handleFieldChange('bussduct', e.target.value)}
                                    disabled={submitted}
                                    sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Bus Plug Number"
                                    value={form.busPlugNumber}
                                    onChange={(e) => handleFieldChange('busPlugNumber', e.target.value)}
                                    disabled={submitted}
                                    inputProps={{ inputMode: 'numeric' }}
                                    sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Column Location"
                                    value={form.columnLocation}
                                    onChange={(e) => handleFieldChange('columnLocation', e.target.value)}
                                    disabled={submitted}
                                    sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Load Served"
                                    value={form.loadServed}
                                    onChange={(e) => handleFieldChange('loadServed', e.target.value)}
                                    disabled={submitted}
                                    sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Bus Plug Size"
                                    value={form.busPlugSize}
                                    onChange={(e) => handleFieldChange('busPlugSize', e.target.value)}
                                    disabled={submitted}
                                    sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                                />
                            </Grid>
                        </Grid>
                    </Card>

                    {/* BUS PLUG CHECKS */}
                    <Card sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                            BUS PLUG CHECKS
                        </Typography>
                        <Grid container>
                            {renderRadioItem('busPlugLabeled', 'Bus Plug labeled with load name and Bus Plug #', [
                                { value: 'temp', label: 'Temp' },
                                { value: 'permanent', label: 'Permanent' }
                            ])}
                            
                            {renderCheckItem('switchSupport', 'Switch Support (200 amp and above)')}
                            {renderCheckItem('switchGeneralInstallation', 'Switch General Installation')}
                            {renderCheckItem('plugAssistEngaged', 'Plug Assist Fully Engaged – IF Applicable')}
                            
                            <Grid item xs={12}>
                                <Alert severity="warning" sx={{ fontSize: '0.75rem', py: 0.5, mb: 1 }}>
                                    THIS MUST BE COORDINATED WITH LOCKOUT/TAGOUT OF DOWNSTREAM LOAD AND MEGGER READINGS DELIVERED. VERIFY BUS IS OFF/LOCKED OUT ETC. DEPENDING ON SITUATION!
                                </Alert>
                            </Grid>
                            
                            {renderCheckItem('functionSwitch', 'Function of Switch On/Off to Check Interlock Slide')}
                            
                            {renderInputWithComment('fuseSize', 'Fuse Size', true)}
                            {renderInputWithComment('fuseType', 'Fuse Type', true)}
                            {renderInputWithComment('conductorSize', 'Conductor Size', true)}
                            {renderInputWithComment('connectionTightnessBusPlug', 'Connection Tightness of Bus Plug', true)}
                            {renderInputWithComment('connectionTightnessLugs', 'Connection Tightness of all Lugs/Conductors', true)}
                            {renderCheckItem('conduitSupportBusPlug', 'Conduit Support at the Bus Plug')}
                        </Grid>
                    </Card>

                    {/* PANEL CHECKS */}
                    <Card sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                            PANEL CHECKS
                        </Typography>
                        <Grid container>
                            {renderRadioItem('panelLabeled', 'Panel labeled with Bus Plug # and Bus Duct ID', [
                                { value: 'temp', label: 'Temp' },
                                { value: 'permanent', label: 'Permanent' }
                            ])}
                            
                            {renderRadioItem('arcFlashSticker', 'ARC FLASH Sticker on Panel', [
                                { value: 'yes', label: 'Yes' },
                                { value: 'no', label: 'No' }
                            ])}
                            
                            <Grid item xs={12}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mt: 1, mb: 1, fontSize: '0.85rem' }}>
                                    Conductor Phasing
                                </Typography>
                            </Grid>
                            {renderCheckItem('phasing_A_brown', 'A = Brown')}
                            {renderCheckItem('phasing_B_orange', 'B = Orange')}
                            {renderCheckItem('phasing_C_yellow', 'C = Yellow')}
                            {renderCheckItem('phasing_GRD_green', 'GRD = Green')}
                            
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Chip label="4 WIRE ONLY" size="small" color="warning" sx={{ fontSize: '0.65rem' }} />
                                </Box>
                            </Grid>
                            {renderCheckItem('phasing_Neutral_white', 'Neutral = Gray')}
                            
                            {renderCheckItem('additionalWiresLineSide', 'Additional wires on line side (Yellow Tape)')}
                            {renderCheckItem('lineSideDistributionBlocks', 'Line side distribution blocks to be finger safe')}
                            {renderCheckItem('plexiglassCover', 'Plexiglass cover with caution label indicating voltage and shock hazard')}
                            {renderInputWithComment('connectionTightnessPanelLugs', 'Connection Tightness of Lugs/Conductors', true)}
                            {renderInputWithComment('conduitBushingInstallation', 'Conduit/Bushing Installation Tightness, Level, Binding, etc. (No EMT Conduit)', true)}
                            {renderCheckItem('groundCable', 'Ground Cable')}
                        </Grid>
                    </Card>

                    {/* MEGGER READINGS */}
                    <Card sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                            Megger Readings (MΩ)
                        </Typography>
                            <Box sx={{ mb: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={form.hasNeutral || false}
                                            onChange={(e) => {
                                                handleFieldChange('hasNeutral', e.target.checked);
                                            }}
                                            disabled={submitted}
                                            size="small"
                                            sx={{
                                                color: '#1976d2',
                                                '&.Mui-checked': {
                                                    color: '#1976d2',
                                                }
                                            }}
                                        />
                                    }
                                    label={<Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>Has Neutral</Typography>}
                                />
                            </Box>
                            <Grid container spacing={1.5}>
                                {[
                                    { field: 'megger_AB', label: 'A-B' },
                                    { field: 'megger_BC', label: 'B-C' },
                                    { field: 'megger_CA', label: 'C-A' },
                                    { field: 'megger_A_GRD', label: 'A-GRD' },
                                    { field: 'megger_B_GRD', label: 'B-GRD' },
                                    { field: 'megger_C_GRD', label: 'C-GRD' }
                                ].map(item => (
                                    <Grid item xs={6} sm={4} key={item.field}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={`${item.label} (MΩ)`}
                                            value={form[item.field]}
                                            onChange={(e) => handleFieldChange(item.field, e.target.value)}
                                            disabled={submitted}
                                            inputProps={{ inputMode: 'decimal' }}
                                            sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                                        />
                                    </Grid>
                                ))}
                                {form.hasNeutral && (
                                    <Grid item xs={6} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="GRD-to-Neutral (MΩ)"
                                            value={form.megger_GRD_Neutral}
                                            onChange={(e) => handleFieldChange('megger_GRD_Neutral', e.target.value)}
                                            disabled={submitted}
                                            inputProps={{ inputMode: 'decimal' }}
                                            sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                                        />
                                    </Grid>
                                )}
                            </Grid>

                            <Alert severity="warning" sx={{ mt: 2, mb: 2, fontSize: '0.8rem', py: 1 }}>
                                It is installing contractor's responsibility to close the bus disconnect. Before closing bus disconnect ensure there is no connected load.
                            </Alert>

                            <Box>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={form.disconnectCheckConfirmed || false}
                                            onChange={(e) => {
                                                handleFieldChange('disconnectCheckConfirmed', e.target.checked);
                                            }}
                                            disabled={submitted}
                                            size="small"
                                            sx={{
                                                color: errors.disconnectCheckConfirmed ? '#f44336' : '#1976d2',
                                                '&.Mui-checked': {
                                                    color: '#1976d2',
                                                }
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" sx={{ 
                                            fontSize: '0.85rem',
                                            fontWeight: errors.disconnectCheckConfirmed ? 600 : 500, 
                                            color: errors.disconnectCheckConfirmed ? 'error.main' : 'inherit' 
                                        }}>
                                            I confirm that disconnect check has been coordinated and verified *
                                        </Typography>
                                    }
                                />
                                {errors.disconnectCheckConfirmed && (
                                    <Typography variant="caption" color="error" display="block" sx={{ ml: 4 }}>
                                        {errors.disconnectCheckConfirmed}
                                    </Typography>
                                )}
                            </Box>
                    </Card>

                    {/* VOLTAGE READINGS & PHASE ROTATION */}
                    <Card sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                            Voltage Readings + Phase Rotation
                        </Typography>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, fontSize: '0.85rem' }}>
                                    System Voltage
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {[
                                        { value: '480/277', label: '480/277' },
                                        { value: '208/120', label: '208/120' }
                                    ].map(opt => (
                                        <Box 
                                            key={opt.value}
                                            onClick={() => !submitted && handleFieldChange('systemVoltage', opt.value)}
                                            sx={{
                                                cursor: submitted ? 'default' : 'pointer',
                                                px: 2,
                                                py: 1,
                                                borderRadius: 1,
                                                border: '1.5px solid',
                                                borderColor: form.systemVoltage === opt.value ? '#1976d2' : '#ccc',
                                                backgroundColor: form.systemVoltage === opt.value ? '#1976d2' : 'transparent',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ 
                                                fontSize: '0.85rem',
                                                fontWeight: 600,
                                                color: form.systemVoltage === opt.value ? 'white' : '#666'
                                            }}>
                                                {opt.label}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                    {form.systemVoltage === '480/277' 
                                        ? 'Expected: Line-Line 480V ±10% (432-528V), Line-Ground 277V ±10% (249-305V)'
                                        : 'Expected: Line-Line 208V ±10% (187-229V), Line-Ground 120V ±10% (108-132V)'}
                                </Typography>
                            </Box>

                            <Grid container spacing={1.5}>
                                {[
                                    { field: 'voltage_AB', label: 'A-B', voltage480: '480', voltage208: '208' },
                                    { field: 'voltage_BC', label: 'B-C', voltage480: '480', voltage208: '208' },
                                    { field: 'voltage_CA', label: 'C-A', voltage480: '480', voltage208: '208' },
                                    { field: 'voltage_A_GRD', label: 'A-GRD', voltage480: '277', voltage208: '120' },
                                    { field: 'voltage_B_GRD', label: 'B-GRD', voltage480: '277', voltage208: '120' },
                                    { field: 'voltage_C_GRD', label: 'C-GRD', voltage480: '277', voltage208: '120' }
                                ].map(item => {
                                    const expectedVoltage = form.systemVoltage === '480/277' ? item.voltage480 : item.voltage208;
                                    return (
                                        <Grid item xs={6} sm={4} key={item.field}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={`${item.label} (${expectedVoltage}V)`}
                                                value={form[item.field]}
                                                onChange={(e) => handleFieldChange(item.field, e.target.value)}
                                                disabled={submitted}
                                                error={!!errors[item.field]}
                                                helperText={errors[item.field]}
                                                inputProps={{ inputMode: 'decimal' }}
                                                sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>

                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 3, mb: 1, fontSize: '0.85rem' }}>
                                Phase Rotation
                            </Typography>
                            <Grid container>
                                {renderRadioItem('phaseRotation', 'Phase Rotation', [
                                    { value: 'CW', label: 'CW' },
                                    { value: 'CCW', label: 'CCW' }
                                ])}
                            </Grid>
                    </Card>

                    {/* NEC */}
                    <Card sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                            NEC
                        </Typography>
                        <Grid container>
                            {renderRadioItemWithComment('anyNECViolations', 'Any NEC Violations?', [
                                { value: 'Y', label: 'Yes' },
                                { value: 'N', label: 'No' }
                            ], 'COMMENT')}
                        </Grid>
                    </Card>

                    {/* Signatures */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Signatures</Typography>
                            
                            {/* Client Signature */}
                            <Box mb={2}>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Client *</Typography>
                                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }} alignItems="center">
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Name *"
                                            value={form.clientSignatureName || ''}
                                            onChange={(e) => handleFieldChange('clientSignatureName', e.target.value)}
                                            disabled={submitted}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Date"
                                            type="date"
                                            value={form.clientSignatureDate}
                                            onChange={(e) => handleFieldChange('clientSignatureDate', e.target.value)}
                                            disabled={submitted}
                                            InputLabelProps={{ shrink: true }}
                                            onClick={(e) => {
                                                if (!submitted) {
                                                    e.currentTarget.querySelector('input')?.showPicker?.();
                                                }
                                            }}
                                            InputProps={{
                                                style: { cursor: submitted ? 'default' : 'pointer' }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box display="flex" justifyContent="center" alignItems="center">
                                            {form.clientSignature ? (
                                                <Box position="relative" display="inline-block">
                                                    <img
                                                        src={form.clientSignature}
                                                        alt="Signature"
                                                        style={{
                                                            maxWidth: 200,
                                                            height: 60,
                                                            border: '1px solid #ddd',
                                                            borderRadius: 4,
                                                            cursor: submitted ? 'default' : 'pointer'
                                                        }}
                                                        onClick={() => {
                                                            if (!submitted) {
                                                                handleSignatureClick('client');
                                                            }
                                                        }}
                                                        title={submitted ? '' : 'Click to edit signature'}
                                                    />
                                                    {!submitted && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleFieldChange('clientSignature', '')}
                                                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                        >
                                                            <ClearIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<DrawIcon />}
                                                    onClick={() => handleSignatureClick('client')}
                                                    disabled={submitted}
                                                >
                                                    Add Signature
                                                </Button>
                                            )}
                                        </Box>
                                        {errors.clientSignature && (
                                            <Typography variant="caption" color="error" display="block" textAlign="center" sx={{ mt: 0.5 }}>
                                                {errors.clientSignature}
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Client Area Manager Signature */}
                            <Box mb={2}>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Client Area Manager</Typography>
                                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }} alignItems="center">
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Name"
                                            value={form.shopTeamSignatureName || ''}
                                            onChange={(e) => handleFieldChange('shopTeamSignatureName', e.target.value)}
                                            disabled={submitted}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Date"
                                            type="date"
                                            value={form.shopTeamSignatureDate}
                                            onChange={(e) => handleFieldChange('shopTeamSignatureDate', e.target.value)}
                                            disabled={submitted}
                                            InputLabelProps={{ shrink: true }}
                                            onClick={(e) => {
                                                if (!submitted) {
                                                    e.currentTarget.querySelector('input')?.showPicker?.();
                                                }
                                            }}
                                            InputProps={{
                                                style: { cursor: submitted ? 'default' : 'pointer' }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box display="flex" justifyContent="center" alignItems="center">
                                            {form.shopTeamSignature ? (
                                                <Box position="relative" display="inline-block">
                                                    <img
                                                        src={form.shopTeamSignature}
                                                        alt="Signature"
                                                        style={{
                                                            maxWidth: 200,
                                                            height: 60,
                                                            border: '1px solid #ddd',
                                                            borderRadius: 4,
                                                            cursor: submitted ? 'default' : 'pointer'
                                                        }}
                                                        onClick={() => {
                                                            if (!submitted) {
                                                                handleSignatureClick('shopTeam');
                                                            }
                                                        }}
                                                        title={submitted ? '' : 'Click to edit signature'}
                                                    />
                                                    {!submitted && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleFieldChange('shopTeamSignature', '')}
                                                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                        >
                                                            <ClearIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<DrawIcon />}
                                                    onClick={() => handleSignatureClick('shopTeam')}
                                                    disabled={submitted}
                                                >
                                                    Add Signature
                                                </Button>
                                            )}
                                        </Box>
                                        {errors.shopTeamSignature && (
                                            <Typography variant="caption" color="error" display="block" textAlign="center" sx={{ mt: 0.5 }}>
                                                {errors.shopTeamSignature}
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Contractor Signature */}
                            <Box mb={0}>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Contractor *</Typography>
                                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }} alignItems="center">
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Name *"
                                            value={form.contractorSignatureName || ''}
                                            onClick={() => !submitted && setContractorSignatureModalEmployeeOpen(true)}
                                            disabled={submitted}
                                            InputProps={{
                                                readOnly: true,
                                                endAdornment: !submitted && (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setContractorSignatureModalEmployeeOpen(true)}
                                                            edge="end"
                                                        >
                                                            <PersonSearchIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                                style: { cursor: submitted ? 'default' : 'pointer' }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Date"
                                            type="date"
                                            value={form.contractorSignatureDate}
                                            onChange={(e) => handleFieldChange('contractorSignatureDate', e.target.value)}
                                            disabled={submitted}
                                            InputLabelProps={{ shrink: true }}
                                            onClick={(e) => {
                                                if (!submitted) {
                                                    e.currentTarget.querySelector('input')?.showPicker?.();
                                                }
                                            }}
                                            InputProps={{
                                                style: { cursor: submitted ? 'default' : 'pointer' }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box display="flex" justifyContent="center" alignItems="center">
                                            {form.contractorSignature ? (
                                                <Box position="relative" display="inline-block">
                                                    <img
                                                        src={form.contractorSignature}
                                                        alt="Signature"
                                                        style={{
                                                            maxWidth: 200,
                                                            height: 60,
                                                            border: '1px solid #ddd',
                                                            borderRadius: 4,
                                                            cursor: submitted ? 'default' : 'pointer'
                                                        }}
                                                        onClick={() => {
                                                            if (!submitted) {
                                                                handleSignatureClick('contractor');
                                                            }
                                                        }}
                                                        title={submitted ? '' : 'Click to edit signature'}
                                                    />
                                                    {!submitted && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleFieldChange('contractorSignature', '')}
                                                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                        >
                                                            <ClearIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<DrawIcon />}
                                                    onClick={() => handleSignatureClick('contractor')}
                                                    disabled={submitted}
                                                >
                                                    Add Signature
                                                </Button>
                                            )}
                                        </Box>
                                        {errors.contractorSignature && (
                                            <Typography variant="caption" color="error" display="block" textAlign="center" sx={{ mt: 0.5 }}>
                                                {errors.contractorSignature}
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Submit Button */}
                    {!submitted && (
                        <Box display="flex" justifyContent="center" mt={2} mb={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                startIcon={<SendIcon />}
                                onClick={handleSubmit}
                                disabled={saving}
                                sx={{ minWidth: 200 }}
                            >
                                {saving ? 'Submitting...' : 'Submit Report'}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Signature Modals */}
            <SignatureModalWithAuto
                open={clientSignatureModalOpen}
                onClose={() => setClientSignatureModalOpen(false)}
                sigPadRef={sigPadRef}
                employeeName={form.clientSignatureName || ''}
                onAccept={() => handleSignatureAccept('client')}
                existingSignature={form.clientSignature}
            />

            <SignatureModalWithAuto
                open={shopTeamSignatureModalOpen}
                onClose={() => setShopTeamSignatureModalOpen(false)}
                sigPadRef={sigPadRef}
                employeeName={form.shopTeamSignatureName || ''}
                onAccept={() => handleSignatureAccept('shopTeam')}
                existingSignature={form.shopTeamSignature}
            />

            <SignatureModalWithAuto
                open={contractorSignatureModalOpen}
                onClose={() => setContractorSignatureModalOpen(false)}
                sigPadRef={sigPadRef}
                employeeName={form.contractorSignatureName || ''}
                onAccept={() => handleSignatureAccept('contractor')}
                existingSignature={form.contractorSignature}
            />
            
            <SelectEmployeesAndTempsModal
                open={contractorModalOpen}
                onClose={() => setContractorModalOpen(false)}
                onSelect={handleSelectContractor}
                singleSelect={true}
                alreadySelected={[]}
            />
            
            <SelectEmployeesAndTempsModal
                open={contractorSignatureModalEmployeeOpen}
                onClose={() => setContractorSignatureModalEmployeeOpen(false)}
                onSelect={handleSelectContractorSignature}
                singleSelect={true}
                alreadySelected={form.contractorSignatureName ? [{ FullName: form.contractorSignatureName }] : []}
            />
        </Box>
    );
};

export default BusInspectionForm;

